import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MagnifyingGlass, Plus, PencilLine, Trash, ArrowLeft, Password, DeviceMobile, DownloadSimple } from "@phosphor-icons/react";
import EmployeeDialog from "@/components/hr/EmployeeDialog";
import { PinDialog, PinsGeneratedDialog } from "@/components/hr/PinDialogs";
import { AdminOnly } from "@/pages/hr/Payroll";

export default function Employees() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pinFor, setPinFor] = useState(null);
  const [generated, setGenerated] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/payroll/employees", { params: { q, status } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  }, [q, status]);

  useEffect(() => { if (user?.role === "admin") load(); }, [load, user]);
  if (user?.role !== "admin") return <AdminOnly />;

  const generatePins = async () => {
    const missing = rows.filter((r) => r.status === "active" && !r.pin_set).length;
    if (!missing) return toast.info("Every active employee already has a PIN — use the key icon on a row to reset one.");
    if (!window.confirm(`Generate random 4-digit punch PINs for ${missing} active employee(s) without a PIN?`)) return;
    try {
      const { data } = await api.post("/payroll/punch/employees/pins/generate", { only_missing: true });
      if (!data.count) { toast.info("All active employees already have a PIN — use the key chip on a row to reset one."); return; }
      setGenerated(data); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const downloadPinSheet = async () => {
    const active = rows.filter((r) => r.status === "active").length;
    if (!active) return toast.info("No active employees to generate PINs for.");
    if (!window.confirm(`Generate a fresh punch PIN for all ${active} active employee(s) and download the sheet?\n\nThis REPLACES any existing PINs — each employee must use their new PIN to punch. PINs are shown only once.`)) return;
    try {
      const { data } = await api.post("/payroll/punch/employees/pins/generate", {});
      if (!data.count) { toast.info("No active employees to generate PINs for."); return; }
      setGenerated(data); await load();
      toast.success(`${data.count} PINs generated — click "Download .csv" to save the sheet`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Remove ${e.name} from the employee master?`)) return;
    try {
      const { data } = await api.delete(`/payroll/employees/${e.id}`);
      toast.success(data.deactivated ? data.detail : "Employee removed");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="space-y-4 p-4" data-testid="employees-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <button className="text-[11px] text-slate-500 hover:text-[#EA580C] flex items-center gap-1 mb-1" onClick={() => navigate("/payroll")} data-testid="employees-back">
            <ArrowLeft size={11} /> Payroll
          </button>
          <h1 className="text-2xl font-bold font-display text-slate-900 tracking-tight">Employee Master</h1>
          <div className="text-[11.5px] text-slate-500">Designation, joining date, PAN and bank details entered here appear on every salary slip. Records auto-sync from each imported register. Punch PINs let employees punch in/out from their phone.</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8" onClick={generatePins} data-testid="employee-generate-pins-btn">
            <DeviceMobile size={13} className="mr-1" /> Generate Punch PINs
          </Button>
          <Button size="sm" variant="outline" className="h-8" onClick={downloadPinSheet} data-testid="employee-download-pins-btn">
            <DownloadSimple size={13} className="mr-1" /> Download PIN Sheet
          </Button>
          <Button size="sm" className="h-8 bg-[#EA580C] hover:bg-[#c94d0a] text-white" onClick={() => setEditing({})} data-testid="employee-add-btn">
            <Plus size={13} className="mr-1" /> Add Employee
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2 flex-wrap">
        <MagnifyingGlass size={14} className="text-slate-400" />
        <Input placeholder="Search name, code, UAN, designation…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 text-[12px] w-80" data-testid="employee-search" />
        {["", "active", "inactive"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`employee-filter-${s || "all"}`}
                  className={`px-2.5 py-1 rounded-sm text-[11px] font-semibold border transition-colors ${status === s ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {s || "All"}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-slate-500">{rows.length} employees</span>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="text-[11.5px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Code</TableHead>
                  <TableHead className="min-w-[180px]">Employee</TableHead>
                  <TableHead className="w-36">Designation</TableHead>
                  <TableHead className="w-28">UAN</TableHead>
                  <TableHead className="w-28">ESI No.</TableHead>
                  <TableHead className="w-24">DOJ</TableHead>
                  <TableHead className="w-40">Bank</TableHead>
                  <TableHead className="text-right w-28">Monthly Gross</TableHead>
                  <TableHead className="text-center w-24">Punch PIN</TableHead>
                  <TableHead className="text-center w-20">Status</TableHead>
                  <TableHead className="w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-10">{busy ? "Loading…" : "No employees yet — import a Salary Register or add one manually."}</TableCell></TableRow>
                )}
                {rows.map((e) => (
                  <TableRow key={e.id} className="hover:bg-slate-50" data-testid={`employee-row-${e.emp_code}`}>
                    <TableCell className="font-mono-num font-semibold text-slate-700">{e.emp_code}</TableCell>
                    <TableCell>
                      <div className="font-semibold text-slate-900">{e.name}</div>
                      <div className="text-[10px] text-slate-500">{e.father_name ? `S/o ${e.father_name}` : ""}{e.department ? ` · ${e.department}` : ""}</div>
                    </TableCell>
                    <TableCell className="text-slate-700">{e.designation || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="font-mono-num text-slate-600">{e.uan || "—"}</TableCell>
                    <TableCell className="font-mono-num text-slate-600">{e.esi_no || "—"}</TableCell>
                    <TableCell className="text-slate-600">{e.doj || "—"}</TableCell>
                    <TableCell className="text-[10.5px] text-slate-600">{e.bank_name ? `${e.bank_name} · ${e.bank_account}` : <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-right font-mono-num">{fmtMoney((e.basic || 0) + (e.hra || 0) + (e.conv || 0))}</TableCell>
                    <TableCell className="text-center">
                      <button onClick={() => setPinFor(e)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[9.5px] font-semibold uppercase tracking-widest border ${e.pin_set ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-white text-slate-400 border-dashed border-slate-300"}`} data-testid={`employee-pin-${e.emp_code}`}>
                        <Password size={10} /> {e.pin_set ? "Set" : "No PIN"}
                      </button>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[9.5px] font-semibold uppercase tracking-widest ${e.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{e.status}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 rounded-sm" onClick={() => setEditing(e)} data-testid={`employee-edit-${e.emp_code}`}><PencilLine size={12} /></Button>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 rounded-sm text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(e)} data-testid={`employee-delete-${e.emp_code}`}><Trash size={12} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {editing && <EmployeeDialog employee={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {pinFor && <PinDialog employee={pinFor} onClose={() => setPinFor(null)} onSaved={() => { setPinFor(null); load(); }} />}
      {generated && <PinsGeneratedDialog result={generated} onClose={() => setGenerated(null)} />}
    </div>
  );
}
